import React, { Component } from 'react';
import { bool, string, number, array, object } from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { I18n } from '@fiverr-private/fiverr_context';
import RecommendedBadge from '../RecommendedBadge';
import { formatPricingFactorValue } from '../../../utils/packagesUtils';
import { getPackageById, getSingleCalculator, isTriplePackage } from '../../../reducers/packages';
import { shouldShowFeature } from '../../../utils/pricingFactors';
import { isValidCalculator } from '../../../utils/calculator';
import { UNLIMITED_REVISION } from '../../../utils/packagesUtils/constants';
import FeatureList from './FeatureList';
import PackageContentCalculator from './PackageContentCalculator';
import Services from './Services';
import CollapsablePackageItem from './CollapsablePackageItem';

import './style.scss';

export const COLLAPSE_FEATURES_WITH_CALC = 5;
export const COLLAPSE_FEATURES_WITHOUT_CALC = 7;

class PackageContent extends Component {

    constructor() {
        super();

        this.i18nPackages = 'gig_page_perseus.packages';
    }

    formatRevisions() {
        const { revisions } = this.props;
        const numOfRevisions = formatPricingFactorValue(revisions.value);
        const revisionKey = numOfRevisions === 1 ? 'revision' : 'revisions';

        return { key: `${this.i18nPackages}.${revisionKey}`, amount: numOfRevisions };
    }

    formatDeliveryDays() {
        const { currentDuration } = this.props;
        const dayStr = currentDuration === 1 ? 'day' : 'days';

        return <I18n k={`${this.i18nPackages}.delivery_${dayStr}`} params={{ amount: currentDuration }}/>;
    }

    filterFeatures() {
        const { features, displayNotIncluded } = this.props;

        return features.filter(({ included }) => shouldShowFeature(included, displayNotIncluded));
    }

    shouldShowRevisions() {
        const { revisions } = this.props;

        return revisions && revisions.value !== 0;
    }

    shouldShowHeader() {
        const { showPrice, title, description } = this.props;

        return showPrice || title || description;
    }

    shouldRenderCollapsableItem(filteredFeatures) {
        const { forceNonCollapsable } = this.props;

        return !forceNonCollapsable && this.isEligibleCollapsableList(filteredFeatures);
    }

    isEligibleCollapsableList(filteredFeatures) {
        const { calculator } = this.props;
        const validCalculator = isValidCalculator(calculator);
        const featuresLength = filteredFeatures.length;

        return (validCalculator && featuresLength > COLLAPSE_FEATURES_WITH_CALC)
            || (!validCalculator && featuresLength > COLLAPSE_FEATURES_WITHOUT_CALC);
    }

    renderRevisions() {
        const shouldShowRevisions = this.shouldShowRevisions();

        if (!shouldShowRevisions) {
            return null;
        }

        const { key: revisionsKey, amount: revisionsAmount } = this.formatRevisions();
        const unlimitedKey = 'gig_page_perseus.packages.unlimited_revisions';

        return (
            <b className="revisions">
                {revisionsAmount === UNLIMITED_REVISION ? <I18n k={unlimitedKey}/> : <I18n k={revisionsKey} params={{ amount: revisionsAmount }}/>}
            </b>
        );
    }

    render() {
        const {
            title,
            description,
            calculator,
            id,
            currentPrice,
            showPrice,
            recommendedType,
            tabItem
        } = this.props;

        const { currencyConverter, general } = this.context;
        const { isStudio } = general;

        const className = classNames('package-content', { 'recommended': !!recommendedType });
        const formattedPrice = currencyConverter.convert(currentPrice);
        const filteredFeatures = this.filterFeatures();

        return (
            <div className={className}>
                {recommendedType && <RecommendedBadge recommendedType={recommendedType}/>}
                {this.shouldShowHeader() &&
                <header>
                    <h3>
                        {title && <b className="title">{title}</b>}
                        {showPrice && <span className="price">{formattedPrice}</span>}
                    </h3>
                    {description && <p>{description}</p>}
                </header>
                }
                <article>
                    <b className="delivery">{this.formatDeliveryDays()}</b>
                    {this.renderRevisions()}
                    {!isStudio &&
                    <>
                        {this.shouldRenderCollapsableItem(filteredFeatures) ?
                            <CollapsablePackageItem {...tabItem}
                                features={filteredFeatures}
                                calculator={calculator}
                                packageId={id}/> :
                            <>
                                <FeatureList features={filteredFeatures} packageId={id}/>
                                <PackageContentCalculator calculator={calculator} packageId={id}/>
                            </>
                        }
                    </>
                    }
                    {isStudio &&
                    <Services features={filteredFeatures} packageId={id}/>
                    }
                </article>
            </div>
        );
    }
}

const mapStateToProps = ({ packages, packagesTabs }, ownProps) => {
    const { tabItems } = packagesTabs;
    const displayNotIncluded = isTriplePackage(packages);
    const packageData = getPackageById(packages, ownProps.id);
    const calculator = getSingleCalculator(packages, ownProps.id);

    return {
        ...packageData,

        // todo: remove when VO package has a title
        title: ownProps.title || packageData.title,
        displayNotIncluded,
        showPrice: ownProps.showPrice,
        calculator,
        tabItem: tabItems.length ? tabItems[0] : {}
    };
};

PackageContent.propTypes = {
    title: string,
    description: string,
    features: array,
    calculator: object,
    id: number,
    currentPrice: number,
    currentDuration: number,
    revisions: object,
    showPrice: bool,
    recommendedType: string,
    displayNotIncluded: bool,
    tabItem: object,
    forceNonCollapsable: bool
};

PackageContent.defaultProps = {
    features: [],
    showPrice: true,
    displayNotIncluded: true,
    forceNonCollapsable: false
};

PackageContent.contextTypes = {
    currencyConverter: object,
    general: object
};

export { PackageContent };
export default connect(mapStateToProps)(PackageContent);
